import { dateV2Service } from '../../../../../domain/value-object/DateV2'

export type SprintInterval = '1w' | '2w' | '1month'

export type GeneratedSprint = {
  name: string
  startDate: string
  endDate: string
}

type BaseParams = {
  count: number
  prefix: string
}

type WeekParams = BaseParams & {
  interval: '1w' | '2w'
  startDate: string // YYYY/MM/DD
}

type MonthParams = BaseParams & {
  interval: '1month'
  startDayOfMonth: number // 1-31
  startDate: string // YYYY/MM/DD
}

export type GenerateSprintsParams = WeekParams | MonthParams

const DISPLAY_FORMAT = 'YYYY/MM/DD'
const NAME_DATE_FORMAT = 'YYMMDD'

const parseDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('/').map(Number)
  return new Date(y, m - 1, d)
}

const formatDisplay = (date: Date): string =>
  dateV2Service.format(date, DISPLAY_FORMAT)

const formatName = (date: Date): string =>
  dateV2Service.format(date, NAME_DATE_FORMAT)

const getMonthStartDate = (
  year: number,
  month: number,
  targetDay: number
): Date => {
  const maxDay = dateV2Service.daysInMonth(year, month)
  const day = Math.min(targetDay, maxDay)
  return new Date(year, month, day)
}

const isWeekParams = (params: GenerateSprintsParams): params is WeekParams =>
  params.interval === '1w' || params.interval === '2w'

export const shiftSprintDates = (
  sprints: GeneratedSprint[],
  fromIndex: number,
  days: number,
  prefix: string
): GeneratedSprint[] => {
  // 押した行の終了日が開始日より前にならないかチェック
  const target = sprints[fromIndex]
  const targetStart = parseDate(target.startDate)
  const targetNewEnd = dateV2Service.addDays(parseDate(target.endDate), days)
  if (dateV2Service.isBefore(targetNewEnd, targetStart)) return sprints

  return sprints.map((sprint, i) => {
    if (i < fromIndex) return sprint
    if (i === fromIndex) {
      return {
        name: sprint.name,
        startDate: sprint.startDate,
        endDate: formatDisplay(targetNewEnd),
      }
    }
    // それ以降: 開始日・終了日ともにシフト
    const newStart = dateV2Service.addDays(parseDate(sprint.startDate), days)
    const newEnd = dateV2Service.addDays(parseDate(sprint.endDate), days)
    return {
      name: `${prefix}${formatName(newStart)}`,
      startDate: formatDisplay(newStart),
      endDate: formatDisplay(newEnd),
    }
  })
}

export const appendSprints = (
  existing: GeneratedSprint[],
  count: number,
  interval: SprintInterval,
  prefix: string
): GeneratedSprint[] => {
  if (count <= 0 || existing.length === 0) return existing
  const last = existing[existing.length - 1]
  const lastEnd = parseDate(last.endDate)
  const nextStart = dateV2Service.addDays(lastEnd, 1)

  const params: GenerateSprintsParams =
    interval === '1month'
      ? {
          interval,
          startDayOfMonth: nextStart.getDate(),
          startDate: formatDisplay(nextStart),
          count,
          prefix,
        }
      : {
          interval,
          startDate: formatDisplay(nextStart),
          count,
          prefix,
        }
  return [...existing, ...generateSprints(params)]
}

export const generateSprints = (
  params: GenerateSprintsParams
): GeneratedSprint[] => {
  const { count, prefix } = params
  if (count <= 0) return []

  const sprints: GeneratedSprint[] = []

  if (isWeekParams(params)) {
    const durationDays = params.interval === '1w' ? 7 : 14
    let currentStart = parseDate(params.startDate)

    for (let i = 0; i < count; i++) {
      const endDate = dateV2Service.addDays(currentStart, durationDays - 1)
      sprints.push({
        name: `${prefix}${formatName(currentStart)}`,
        startDate: formatDisplay(currentStart),
        endDate: formatDisplay(endDate),
      })
      currentStart = dateV2Service.addDays(endDate, 1)
    }
  } else {
    const baseDate = parseDate(params.startDate)
    let currentYear = baseDate.getFullYear()
    let currentMonth = baseDate.getMonth()
    const targetDay = params.startDayOfMonth

    for (let i = 0; i < count; i++) {
      const currentStart = getMonthStartDate(
        currentYear,
        currentMonth,
        targetDay
      )
      let nextMonth = currentMonth + 1
      let nextYear = currentYear
      if (nextMonth > 11) {
        nextMonth = 0
        nextYear++
      }
      const nextStart = getMonthStartDate(nextYear, nextMonth, targetDay)
      const endDate = dateV2Service.addDays(nextStart, -1)

      sprints.push({
        name: `${prefix}${formatName(currentStart)}`,
        startDate: formatDisplay(currentStart),
        endDate: formatDisplay(endDate),
      })

      currentYear = nextYear
      currentMonth = nextMonth
    }
  }

  return sprints
}
